<template>
  <v-container fluid class="pa-0 rag-management-page">
    <!-- Top Toolbar -->
    <v-app-bar dense flat color="white" class="border-bottom rag-toolbar">
      <v-container class="d-flex align-center py-0">
        <div class="d-flex flex-column flex-sm-row justify-space-between w-100">
          <v-toolbar-title class="text-h5 font-weight-bold">
            <v-icon class="mr-2" color="#00636C"
              >mdi-chart-box-multiple-outline</v-icon
            >
            Wissensdatenbank
          </v-toolbar-title>
          <v-spacer />
          <v-btn outlined color="#00636C" @click="openGroupDialog" class="mr-2">
            <v-icon small class="mr-1">mdi-plus</v-icon>
            Neue Gruppe
          </v-btn>
          <v-btn
            color="white"
            :style="{ backgroundColor: '#00636C' }"
            @click="showForm = !showForm"
          >
            <v-icon small class="mr-1">{{
              showForm ? "mdi-minus" : "mdi-plus"
            }}</v-icon>
            {{ showForm ? "Formular schließen" : "Neue Datenquelle" }}
          </v-btn>
        </div>
      </v-container>
    </v-app-bar>

    <!-- Main Content -->
    <v-container class="pt-6">
      <!-- Pipeline Form -->
      <v-slide-x-transition>
        <v-row v-if="showForm" ref="pipelineFormSection" class="mb-6">
          <v-col cols="12">
            <v-card elevation="2" class="pa-6">
              <v-card-title class="text-h5 font-weight-bold mb-4">
                {{
                  isEditing
                    ? "Datenquelle bearbeiten"
                    : "Neue Datenquelle erstellen"
                }}
              </v-card-title>

              <v-checkbox
                v-model="form.cj_enabled"
                label="Enable Cronjob"
                color="#00636C"
                hide-details="auto"
                class="mb-4 mt-0"
              />

              <v-row>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="form.pipeline_group"
                    :items="pipelineGroups"
                    item-text="name"
                    item-value="_id"
                    label="Gruppe (optional)"
                    outlined
                    dense
                    hide-details="auto"
                    :clearable="true"
                    class="mb-4"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="form.type"
                    :items="typeOptions"
                    item-text="text"
                    item-value="value"
                    label="Typ (erforderlich)"
                    outlined
                    dense
                    hide-details="auto"
                    class="mb-4"
                  />
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.name"
                    label="Name"
                    outlined
                    dense
                    hide-details="auto"
                    class="mb-4"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.description"
                    label="Beschreibung"
                    outlined
                    dense
                    hide-details="auto"
                    class="mb-4"
                  />
                </v-col>
              </v-row>

              <v-row v-if="isWebsiteSelected">
                <v-col cols="12" md="4">
                  <v-select
                    v-model="form.websiteSource"
                    :items="websiteSourceOptions"
                    item-text="text"
                    item-value="value"
                    label="Quelle"
                    outlined
                    dense
                    hide-details="auto"
                    class="mb-4"
                  />
                </v-col>
                <v-col cols="12" md="8" v-if="form.websiteSource === 'sitemap'">
                  <v-text-field
                    v-model="form.sitemap"
                    label="Sitemap URL"
                    outlined
                    dense
                    hide-details="auto"
                    class="mb-4"
                  />
                </v-col>
                <v-col cols="12" md="8" v-else>
                  <v-combobox
                    v-model="form.includeURLs"
                    label="Include URLs"
                    multiple
                    chips
                    deletable-chips
                    outlined
                    dense
                    hide-details="auto"
                    class="mb-4"
                    hint="Mehrere URLs mit Enter hinzufügen"
                    persistent-hint
                  />
                </v-col>
              </v-row>

              <v-row v-if="isRssFeedSelected">
                <v-col cols="12">
                  <v-text-field
                    v-model="form.rssFeed"
                    label="RSS Feed URL"
                    outlined
                    dense
                    hide-details="auto"
                    class="mb-4"
                  />
                </v-col>
              </v-row>

              <v-row v-if="isEONewsSelected">
                <v-col cols="12" md="6">
                  <v-select
                    v-model="form.eoNewsApiKey"
                    :items="apiKeys"
                    item-text="description"
                    item-value="apiKey"
                    label="News API Key"
                    outlined
                    dense
                    hide-details="auto"
                    class="mb-4"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.newsBaseURL"
                    label="News Base URL"
                    outlined
                    dense
                    hide-details="auto"
                    class="mb-4"
                  />
                </v-col>
              </v-row>

              <v-row v-if="isEOPoisSelected">
                <v-col cols="12" md="6">
                  <v-select
                    v-model="form.poiApiKey"
                    :items="apiKeys"
                    item-text="description"
                    item-value="apiKey"
                    label="POI API Key"
                    outlined
                    dense
                    hide-details="auto"
                    class="mb-4"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.poiBaseURL"
                    label="POI Base URL"
                    outlined
                    dense
                    hide-details="auto"
                    class="mb-4"
                  />
                </v-col>
              </v-row>

              <v-row v-if="isEOEventsSelected">
                <v-col cols="12" md="6">
                  <v-select
                    v-model="form.eventsApiKey"
                    :items="apiKeys"
                    item-text="description"
                    item-value="apiKey"
                    label="Events API Key"
                    outlined
                    dense
                    hide-details="auto"
                    class="mb-4"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.eventsBaseURL"
                    label="Events Base URL"
                    outlined
                    dense
                    hide-details="auto"
                    class="mb-4"
                  />
                </v-col>
              </v-row>

              <v-row v-if="isEOKisSelected">
                <v-col cols="12" md="6">
                  <v-select
                    v-model="form.kisApiKey"
                    :items="apiKeys"
                    item-text="description"
                    item-value="apiKey"
                    label="KIS API Key"
                    outlined
                    dense
                    hide-details="auto"
                    class="mb-4"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.clubBaseURL"
                    label="Vereine URL"
                    outlined
                    dense
                    hide-details="auto"
                    class="mb-4"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.committeeBaseURL"
                    label="Gremien URL"
                    outlined
                    dense
                    hide-details="auto"
                    class="mb-4"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.departmentBaseURL"
                    label="Geschaeftsbereiche URL"
                    outlined
                    dense
                    hide-details="auto"
                    class="mb-4"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.facilityBaseURL"
                    label="Einrichtungen URL"
                    outlined
                    dense
                    hide-details="auto"
                    class="mb-4"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.personBaseURL"
                    label="Personen URL"
                    outlined
                    dense
                    hide-details="auto"
                    class="mb-4"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.taskBaseURL"
                    label="Dienstleistungen URL"
                    outlined
                    dense
                    hide-details="auto"
                    class="mb-4"
                  />
                </v-col>
              </v-row>

              <v-row v-if="isTomasSelected">
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.tportalSourceURL"
                    label="T-Portal Source URL"
                    outlined
                    dense
                    hide-details="auto"
                    class="mb-4"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.thomasBaseURL"
                    label="Thomas Base URL"
                    outlined
                    dense
                    hide-details="auto"
                    class="mb-4"
                  />
                </v-col>
              </v-row>

              <v-file-input
                v-if="isDocumentsSelected"
                v-model="pdfFile"
                accept=".pdf"
                label="PDFs hochladen"
                outlined
                dense
                hide-details="auto"
                multiple
                class="mb-4"
              />

              <div
                v-if="
                  isDocumentsSelected && form.documents && form.documents.length
                "
              >
                <div class="text-subtitle-2 font-weight-bold mb-2">
                  Bereits hochgeladene Dokumente:
                </div>
                <v-chip
                  v-for="doc in form.documents"
                  :key="doc.url"
                  class="ma-1"
                  color="#00636C"
                  text-color="white"
                >
                  {{ doc.name }}
                </v-chip>
              </div>
            </v-card>

            <v-row class="mt-2">
              <v-col class="d-flex justify-end align-center">
                <v-btn
                  @click="isEditing ? updatePipeline() : addPipeline()"
                  class="mr-2"
                >
                  {{
                    isEditing
                      ? "Datenquelle aktualisieren"
                      : "Datenquelle erstellen"
                  }}
                </v-btn>
                <v-btn v-if="isEditing" @click="cancelEdit">
                  Abbrechen
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-slide-x-transition>

      <!-- Gruppen & Pipelines Grid -->
      <v-row>
        <!-- Gruppen Card -->
        <v-col cols="12" lg="4" class="mb-6">
          <v-card elevation="2" class="h-100">
            <v-card-title class="text-h6 font-weight-bold d-flex align-center">
              <v-icon class="mr-2" color="#00636C">mdi-folder-multiple</v-icon>
              Datenquellen-Gruppen ({{ pipelineGroups.length }})
            </v-card-title>
            <v-divider />

            <v-list dense v-if="pipelineGroups.length">
              <v-list-item-group>
                <v-list-item
                  v-for="group in pipelineGroups"
                  :key="group._id"
                  class="mb-2"
                >
                  <v-list-item-content>
                    <div class="d-flex align-center text-wrap">
                      <div class="font-weight-bold text-subtitle-2 mr-2">
                        {{ group.name }}
                      </div>
                      <v-chip
                        v-if="group.promptName"
                        small
                        color="#00636C"
                        text-color="white"
                        label
                      >
                        {{ group.promptName }}
                      </v-chip>
                    </div>
                  </v-list-item-content>

                  <v-list-item-action>
                    <v-menu bottom left>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          small
                          v-bind="attrs"
                          v-on="on"
                          color="grey darken-1"
                        >
                          <v-icon small>mdi-dots-vertical</v-icon>
                        </v-btn>
                      </template>
                      <v-list dense>
                        <v-list-item @click="editGroup(group)">
                          <v-list-item-icon>
                            <v-icon small>$penOutlined</v-icon>
                          </v-list-item-icon>
                          <v-list-item-title>Bearbeiten</v-list-item-title>
                        </v-list-item>
                        <v-list-item @click="copyGroup(group)">
                          <v-list-item-icon>
                            <v-icon small>mdi-content-copy</v-icon>
                          </v-list-item-icon>
                          <v-list-item-title>Kopieren</v-list-item-title>
                        </v-list-item>
                        <v-list-item @click="confirmDeleteGroup(group)">
                          <v-list-item-icon>
                            <v-icon small color="red">mdi-delete</v-icon>
                          </v-list-item-icon>
                          <v-list-item-title class="red--text"
                            >Löschen</v-list-item-title
                          >
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </v-list-item-action>
                </v-list-item>
              </v-list-item-group>
            </v-list>
            <v-card-text v-else class="text-center grey--text">
              Keine Gruppen vorhanden
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Pipelines Card -->
        <v-col cols="12" lg="8">
          <v-card elevation="2">
            <v-card-title class="text-h6 font-weight-bold d-flex align-center">
              <v-icon class="mr-2" color="#00636C">mdi-pipe</v-icon>
              Datenquellen ({{ pipelines.length }})
            </v-card-title>
            <v-divider />

            <v-list two-line v-if="groupedPipelines.length">
              <template v-for="(group, groupIndex) in groupedPipelines">
                <div
                  v-if="group.pipelines && group.pipelines.length"
                  :key="`group-${group._id}`"
                >
                  <div
                    class="d-flex align-center justify-space-between mt-4"
                    style="padding: 16px 16px 8px 16px;"
                  >
                    <v-subheader
                      class="font-weight-bold text-h5 mb-0"
                      style="height: auto; padding: 0;"
                    >
                      {{ group.name }}
                    </v-subheader>
                    <v-btn
                      outlined
                      color="#00636C"
                      @click="openQuestionDialog(group)"
                    >
                      <v-icon class="mr-1">mdi-help-circle-outline</v-icon>
                      Frage
                    </v-btn>
                  </div>

                  <v-list-item
                    v-for="pipeline in group.pipelines"
                    :key="pipeline._id"
                    class="mb-3"
                    style="height: fit-content"
                  >
                    <v-list-item-content>
                      <v-list-item-title
                        class="font-weight-bold text-subtitle-1 mb-2 text-wrap"
                      >
                        {{ pipeline.name }}

                        <v-chip
                          v-if="pipeline.type"
                          color="#00636C"
                          text-color="white"
                          class="ml-2"
                        >
                          {{ pipeline.type }}
                        </v-chip>
                        <v-chip
                          v-if="pipeline.cj_enabled"
                          color="#1B5E20"
                          text-color="white"
                          class="ml-2"
                        >
                          Cronjob aktiv
                        </v-chip>
                      </v-list-item-title>

                      <v-list-item-subtitle class="text-caption text-wrap">
                        <div v-if="pipeline.description" class="mb-1">
                          {{ pipeline.description }}
                        </div>

                        <div
                          v-if="pipeline.type === 'eo-news'"
                          class="grey--text text--darken-1"
                        >
                          <v-icon x-small>mdi-newspaper</v-icon>
                          News API Key konfiguriert
                        </div>

                        <div
                          v-if="pipeline.type === 'eo-pois'"
                          class="grey--text text--darken-1"
                        >
                          <v-icon x-small>mdi-map-marker</v-icon>
                          POI API Key konfiguriert
                        </div>

                        <div
                          v-if="pipeline.type === 'rss-feed'"
                          class="grey--text text--darken-1"
                        >
                          <v-icon x-small>mdi-rss</v-icon>
                          RSS Feed: {{ pipeline.rssFeed || "nicht gesetzt" }}
                        </div>

                        <div
                          v-if="pipeline.type === 'eo-kis'"
                          class="grey--text text--darken-1"
                        >
                          <v-icon x-small>mdi-domain</v-icon>
                          KIS API Key konfiguriert
                        </div>

                        <div
                          v-if="pipeline.type === 'tomas'"
                          class="grey--text text--darken-1"
                        >
                          <v-icon x-small>mdi-link-variant</v-icon>
                          TOMAS URLs konfiguriert
                        </div>

                        <div v-if="pipeline.type === 'documents'" class="mt-1">
                          <v-icon x-small class="mr-1"
                            >mdi-file-multiple</v-icon
                          >
                          {{ pipeline.documents?.length || 0 }} Dokument(e)
                        </div>

                        <div
                          v-if="pipeline.last_indexed"
                          class="mt-2 grey--text text--darken-1"
                        >
                          <v-icon x-small>mdi-clock-outline</v-icon>
                          Zuletzt indiziert:
                          {{ formatDate(pipeline.last_indexed) }}
                        </div>
                        <div
                          v-if="pipeline?._additional?.totalChunks != null"
                          class="grey--text text--darken-1"
                        >
                          <v-icon x-small>mdi-view-grid</v-icon>
                          Chunks indiziert:
                          {{ pipeline._additional.totalChunks }}
                        </div>
                        <div
                          v-if="Number(pipeline.boost) > 0"
                          class="grey--text text--darken-1"
                        >
                          <v-icon x-small>mdi-tune</v-icon>
                          Boost: {{ Number(pipeline.boost).toFixed(2) }}
                        </div>
                      </v-list-item-subtitle>

                      <div class="d-flex gap-2 flex-wrap mt-3 ">
                        <v-btn
                          outlined
                          color="#00636C"
                          :loading="isPipelineIndexing(pipeline._id)"
                          :disabled="isPipelineIndexing(pipeline._id)"
                          @click="indexPipeline(pipeline)"
                        >
                          <v-icon class="mr-1">mdi-refresh</v-icon>
                          Indizieren
                        </v-btn>
                        <v-btn
                          outlined
                          color="grey darken-1"
                          class="ml-2"
                          :loading="
                            pipelineInfoLoading &&
                              pipelineInfoTargetId === pipeline._id
                          "
                          :disabled="
                            pipelineInfoLoading &&
                              pipelineInfoTargetId === pipeline._id
                          "
                          @click="openPipelineInfoDialog(pipeline)"
                        >
                          <v-icon class="mr-1">mdi-information-outline</v-icon>
                          Info
                        </v-btn>
                      </div>
                    </v-list-item-content>

                    <v-list-item-action>
                      <v-menu bottom left>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            icon
                            small
                            v-bind="attrs"
                            v-on="on"
                            color="grey darken-1"
                          >
                            <v-icon small>mdi-dots-vertical</v-icon>
                          </v-btn>
                        </template>
                        <v-list dense>
                          <v-list-item @click="editPipeline(pipeline)">
                            <v-list-item-icon>
                              <v-icon small>$penOutlined</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title>Bearbeiten</v-list-item-title>
                          </v-list-item>
                          <v-list-item @click="copyPipeline(pipeline)">
                            <v-list-item-icon>
                              <v-icon small>mdi-content-copy</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title>Kopieren</v-list-item-title>
                          </v-list-item>
                          <v-list-item @click="confirmDelete(pipeline)">
                            <v-list-item-icon>
                              <v-icon small color="red">mdi-delete</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title class="red--text"
                              >Löschen</v-list-item-title
                            >
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </v-list-item-action>
                  </v-list-item>

                  <v-divider
                    v-if="
                      groupIndex < groupedPipelines.length - 1 &&
                        groupedPipelines[groupIndex + 1]?.pipelines?.length
                    "
                    class="my-2"
                  />
                </div>
              </template>
            </v-list>

            <v-card-text v-else class="text-center grey--text">
              Keine Datenquellen vorhanden. Erstellen Sie eine neue Datenquelle
              mit dem Button oben.
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Confirm Delete Dialog -->
    <v-dialog
      v-model="deleteDialog"
      max-width="420"
      class="rag-dialog rag-dialog--auto"
    >
      <v-card class="rag-dialog-card">
        <v-card-title class="rag-dialog-title">
          <span class="headline">Confirm Deletion</span>
          <v-btn icon small @click="deleteDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          Are you sure you want to delete
          <strong>{{ pipelineToDelete?.name }}</strong
          >?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="red" text @click="deletePipeline">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Ask Question Dialog -->
    <v-dialog
      v-model="questionDialog"
      width="640"
      max-width="640"
      class="rag-dialog rag-dialog--auto"
    >
      <v-card class="rag-dialog-card rag-question-dialog-card">
        <v-card-title class="rag-dialog-title">
          <span class="headline">Frage an "{{ activeGroup?.name }}"</span>
          <v-btn icon small @click="closeQuestionDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="rag-question-body">
          <v-textarea
            v-model="questionText"
            label="Ihre Frage"
            rows="3"
            auto-grow
            outlined
          />

          <div class="rag-question-submit">
            <v-btn
              color="primary"
              :loading="questionLoading"
              :disabled="!questionText"
              @click="submitQuestion"
            >
              Fragen
            </v-btn>
          </div>

          <div v-if="questionError" class="error--text mt-2">
            {{ questionError }}
          </div>

          <div v-if="questionResults.length" class="mt-4">
            <div class="subtitle-2 font-weight-bold mb-2">
              Relevant passages
            </div>

            <v-list dense>
              <v-list-item
                v-for="(r, idx) in questionResults"
                :key="idx"
                class="mb-2"
              >
                <v-list-item-content>
                  <v-list-item-title class="font-weight-bold">
                    {{ r.title || r.url || "Result " + (idx + 1) }}
                  </v-list-item-title>

                  <v-list-item-subtitle>
                    <div class="grey--text text--darken-1">
                      Score: {{ r.score != null ? r.score.toFixed(4) : "n/a" }}
                    </div>

                    <div
                      class="mt-1 grey--text text--darken-1"
                      v-if="r.similarityScore != null"
                    >
                      Similarity Score: {{ r.similarityScore.toFixed(4) }}
                    </div>

                    <div
                      class="mt-1 grey--text text--darken-1"
                      v-if="r.recencyScore != null"
                    >
                      Recency Score: {{ r.recencyScore.toFixed(4) }}
                    </div>

                    <div class="mt-2" style="white-space: pre-line;">
                      {{ r.text }}
                    </div>

                    <div v-if="r.url" class="mt-2">
                      <strong>Source:</strong> {{ r.url }}
                    </div>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </div>

          <div v-if="questionAnswer" class="mt-6">
            <div class="subtitle-2 font-weight-bold mb-2">Antwort</div>
            <div style="white-space: pre-line">{{ questionAnswer }}</div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Pipeline Info Dialog -->
    <v-dialog
      v-model="pipelineInfoDialog"
      width="760"
      max-width="760"
      scrollable
      class="rag-dialog rag-dialog--scroll"
    >
      <v-card class="rag-dialog-card">
        <v-card-title class="rag-dialog-title">
          <span class="headline">Info</span>
          <v-btn icon small @click="closePipelineInfoDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pipeline-info-content">
          <v-progress-linear
            v-if="pipelineInfoLoading"
            indeterminate
            color="primary"
            class="mb-4"
          />
          <div v-else class="pipeline-info-scroll">
            <div v-if="pipelineInfoError" class="error--text">
              {{ pipelineInfoError }}
            </div>
            <div v-else-if="pipelineInfoData">
              <v-list dense>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">
                      {{ pipelineInfoData.name }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="long-text">
                      {{ pipelineInfoData.description || "—" }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-divider />
                <v-list-item>
                  <v-list-item-content>
                    <div>
                      <strong>Typ:</strong> {{ pipelineInfoData.type || "—" }}
                    </div>
                    <div v-if="pipelineInfoData.group">
                      <strong>Gruppe:</strong>
                      {{ pipelineInfoData.group }}
                    </div>
                    <div v-if="pipelineInfoData.last_indexed">
                      <strong>Zuletzt indiziert:</strong>
                      {{ formatDate(pipelineInfoData.last_indexed) }}
                    </div>
                    <div
                      v-if="pipelineInfoData?._additional?.totalChunks != null"
                    >
                      <strong>Chunks indiziert:</strong>
                      {{ pipelineInfoData._additional.totalChunks }}
                    </div>
                  </v-list-item-content>
                </v-list-item>
                <v-divider />
                <v-list-item>
                  <v-list-item-content>
                    <!-- Chunk Count -->
                    <div class="my-2">
                      <div class="font-weight-bold">
                        Chunks indiziert:
                        {{
                          pipelineInfoData?._additional?.totalChunks ??
                            pipelineInfoData.details?.chunkCount ??
                            0
                        }}
                      </div>
                    </div>

                    <!-- Entries (Dokumente/URLs) -->
                    <div
                      v-if="pipelineInfoData.details?.entries?.length"
                      class="my-2"
                    >
                      <div class="font-weight-bold mb-2">
                        Indizierte Quellen ({{
                          pipelineInfoData.details.entries.length
                        }})
                      </div>
                      <v-list dense>
                        <v-list-item
                          v-for="entry in pipelineInfoData.details.entries"
                          :key="entry.docId"
                          class="align-start"
                        >
                          <v-list-item-content>
                            <div class="font-weight-bold long-text">
                              {{ entry.title || entry.url || entry.docId }}
                            </div>
                            <div class="grey--text text--darken-1 long-text">
                              {{ entry.url }}
                            </div>
                            <div class="grey--text text--darken-1 text-caption">
                              Chunks: {{ entry.chunkCount }}
                            </div>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </div>

                    <!-- Documents (only for documents type) -->
                    <div
                      v-if="pipelineInfoData.details?.documents?.length"
                      class="my-2"
                    >
                      <div class="font-weight-bold mb-2">
                        Hochgeladene Dokumente
                      </div>
                      <v-list dense>
                        <v-list-item
                          v-for="doc in pipelineInfoData.details.documents"
                          :key="doc.id || doc.url"
                          class="align-start"
                        >
                          <v-list-item-content>
                            <div class="font-weight-bold long-text">
                              {{ doc.name || doc.url }}
                            </div>
                            <div class="grey--text text--darken-1">
                              Status: {{ doc.state }}
                            </div>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </div>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </div>
            <div v-else>
              Keine Informationen geladen.
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Ingest Info Dialog -->
    <v-dialog
      v-model="ingestInfoDialog"
      width="640"
      max-width="640"
      class="rag-dialog rag-dialog--scroll"
    >
      <v-card class="rag-dialog-card">
        <v-card-title class="rag-dialog-title">
          <span class="headline">{{ ingestInfoTitle || "Info" }}</span>
          <v-btn icon small @click="closeIngestInfoDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-alert type="info" dense text class="mb-0">
            {{ ingestInfoMessage }}
          </v-alert>

          <v-simple-table v-if="ingestInfoStats.length" dense class="mt-4">
            <template v-slot:default>
              <tbody>
                <tr v-for="stat in ingestInfoStats" :key="stat.label">
                  <td class="font-weight-bold" style="width: 45%;">
                    {{ stat.label }}
                  </td>
                  <td>{{ stat.value }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <div v-if="ingestInfoItems.length" class="mt-4">
            <div class="subtitle-2 font-weight-bold mb-2">Dokument-Details</div>
            <v-list dense>
              <v-list-item
                v-for="item in ingestInfoItems"
                :key="item.id"
                class="px-0"
              >
                <v-list-item-content>
                  <v-list-item-title class="font-weight-bold long-text">
                    {{ item.id }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    Status: {{ item.status }} | Chunks: {{ item.chunks }} |
                    Upserted: {{ item.upserted }} | Modified:
                    {{ item.modified }} | Matched: {{ item.matched }} |
                    Geloescht stale: {{ item.deletedStale }} | Geloescht
                    trailing: {{ item.deletedTrailing }} | Geloescht gesamt:
                    {{ item.deletedTotal }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- New/Edit Group Dialog -->
    <v-dialog
      v-model="showGroupDialog"
      max-width="520"
      class="rag-dialog rag-dialog--auto"
    >
      <v-card class="rag-dialog-card">
        <v-card-title class="rag-dialog-title">
          <span class="headline">{{
            editingId ? "Gruppe bearbeiten" : "Neue Gruppe anlegen"
          }}</span>
          <v-btn icon small @click="showGroupDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="newGroupName"
            label="Gruppenname"
            class="mb-3"
          />
          <v-select
            v-model="newGroupPromptShortId"
            :items="prompts"
            item-text="name"
            item-value="shortId"
            label="Prompt (optional)"
            outlined
            dense
            hide-details="auto"
            :loading="promptsLoading"
            class="mb-3"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showGroupDialog = false">Abbrechen</v-btn>
          <v-btn
            color="primary"
            text
            @click="editingId ? updateGroup() : createGroup()"
          >
            {{ editingId ? "Aktualisieren" : "Anlegen" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Group Dialog -->
    <v-dialog
      v-model="deleteGroupDialog"
      max-width="420"
      class="rag-dialog rag-dialog--auto"
    >
      <v-card class="rag-dialog-card">
        <v-card-title class="rag-dialog-title">
          <span class="headline">Gruppe löschen</span>
          <v-btn icon small @click="deleteGroupDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          Sind Sie sicher, dass Sie die Gruppe
          <strong>{{ groupToDelete?.name }}</strong> löschen möchten?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteGroupDialog = false">Abbrechen</v-btn>
          <v-btn color="red" text @click="deleteGroup">Löschen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import RAGManagement from "./RAGManagement.js";
import moment from "moment";

moment.locale("de");

export default {
  ...RAGManagement,
  methods: {
    ...RAGManagement.methods,
    formatDate(date) {
      if (!date) return "—";
      return moment(date).format("DD. MMMM YYYY, HH:mm");
    }
  }
};
</script>

<style scoped>
.rag-toolbar {
  padding: 0 !important;
}
.pipeline-info-content {
  padding-top: 16px;
}
.pipeline-info-scroll {
  max-height: 65vh;
  overflow-y: auto;
  padding-right: 4px;
}
.rag-dialog-title {
  display: flex;
  flex-direction: row !important;
  align-items: center !important;
  justify-content: space-between !important;
  height: auto !important;
  min-height: 0 !important;
  padding: 16px 20px !important;
  gap: 8px;
}
.rag-dialog-title .headline {
  margin-right: auto;
}
.rag-dialog-card {
  max-height: calc(100vh - 48px);
  overflow-y: auto;
}

.rag-question-body {
  max-height: calc(100vh - 220px);
  overflow-y: auto;
}
.rag-question-submit {
  margin-top: 12px;
}
.long-text {
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}
</style>
